/**
 * Session + idle tracking. Maintains a per-tab session id and tracks "active"
 * milliseconds so heartbeat events report real working time, not wall-clock.
 *
 * Active = there was input within the last `IDLE_THRESHOLD_MS` AND the tab is
 *          currently visible.
 * Session ends = no activity for `SESSION_IDLE_GAP_MS`. The next activity mints
 *                a new session id.
 *
 * Single setInterval polls a coalesced `lastInputAt` timestamp updated by every
 * input event. We don't fire from `mousemove` directly because it can dispatch
 * hundreds of times per second.
 */

const SESSION_IDLE_GAP_MS = 5 * 60 * 1000; // 5 min
const IDLE_THRESHOLD_MS = 5 * 1000;        // 5s with no input = idle
const POLL_INTERVAL_MS = 1000;             // 1s tick for active-time accounting

const INPUT_EVENTS = [
  'mousemove',
  'keydown',
  'click',
  'scroll',
  'touchstart',
  'wheel',
] as const;

export interface SessionAPI {
  /** Return the current session id, minting a new one if the previous session
   *  has timed out. Call before emitting any event so the id is stable. */
  getOrRefreshSessionId(): { sessionId: string; freshlyStarted: boolean };
  /** Pull accumulated active ms and reset the counter. */
  flushActiveMs(): number;
  /** Tear down listeners + timers. Test cleanup or app shutdown. */
  destroy(): void;
}

function mintSessionId(userId: string): string {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${userId}-${Date.now()}-${rand}`;
}

export function startSession(userId: string): SessionAPI {
  let sessionId = mintSessionId(userId);
  let lastInputAt = Date.now();
  let lastTickAt = Date.now();
  let accumulatedActiveMs = 0;
  let nextCallMintsNewSession = false;

  const onInput = () => {
    lastInputAt = Date.now();
  };
  for (const ev of INPUT_EVENTS) {
    window.addEventListener(ev, onInput, { passive: true });
  }

  const onVisibility = () => {
    // Treat a hide as an idle gap — reset the tick clock so we don't credit
    // background time as active when the user comes back.
    lastTickAt = Date.now();
  };
  document.addEventListener('visibilitychange', onVisibility);

  const interval = window.setInterval(() => {
    const now = Date.now();
    const isVisible = document.visibilityState === 'visible';
    const isActive = isVisible && now - lastInputAt < IDLE_THRESHOLD_MS;
    if (isActive) {
      accumulatedActiveMs += now - lastTickAt;
    }
    lastTickAt = now;
    if (now - lastInputAt > SESSION_IDLE_GAP_MS) {
      nextCallMintsNewSession = true;
    }
  }, POLL_INTERVAL_MS);

  return {
    getOrRefreshSessionId() {
      if (nextCallMintsNewSession) {
        sessionId = mintSessionId(userId);
        nextCallMintsNewSession = false;
        // Treat this call as fresh input so we don't immediately re-expire.
        lastInputAt = Date.now();
        lastTickAt = Date.now();
        return { sessionId, freshlyStarted: true };
      }
      return { sessionId, freshlyStarted: false };
    },
    flushActiveMs() {
      const out = accumulatedActiveMs;
      accumulatedActiveMs = 0;
      return out;
    },
    destroy() {
      window.clearInterval(interval);
      for (const ev of INPUT_EVENTS) {
        window.removeEventListener(ev, onInput);
      }
      document.removeEventListener('visibilitychange', onVisibility);
    },
  };
}

// Constants exposed for tests.
export const _internals = {
  SESSION_IDLE_GAP_MS,
  IDLE_THRESHOLD_MS,
  POLL_INTERVAL_MS,
  INPUT_EVENTS,
};
